'use memo';

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import type { ApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DeviceDetailFormStore, DeviceDetailSectionForm, useDeviceDetailFormStore, useGetDeviceProfileHeader, useGetDeviceStatus, useSaveDeviceStatus } from '../-shared';
import { StatusTimeline } from './-components/StatusTimeline';
import { splitStatusSections } from './-utils/splitStatusSections';
export const Route = createFileRoute('/_staff/devices/$id/_layout/status/')({
  component: DeviceStatusTab
});
function DeviceStatusTab() {
  const {
    id
  } = useParams({
    from: '/_staff/devices/$id/_layout/status/'
  });
  const store = useMemo(() => DeviceDetailFormStore.createStore(), []);
  return <DeviceDetailFormStore.Provider value={store}>
      <DeviceStatusTabContent id={id} />
    </DeviceDetailFormStore.Provider>;
}
function SectionDetailFetchError({
  error
}: {
  error: Error;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.sectionDetailLayout'
  });
  return <div className="p-6">
      <VStack className="gap-4 items-center">
        <Text variant="h3" className="text-error-on-neutral-fill">
          {t('errorTitle')}
        </Text>
        <Text variant="p" className="text-neutral-muted-on-fill">
          {error.message}
        </Text>
      </VStack>
    </div>;
}
function DeviceStatusTabContent({
  id
}: {
  id: string;
}) {
  const {
    data,
    isPending,
    error
  } = useGetDeviceStatus(id);
  const {
    data: headerData,
    isPending: isHeaderPending,
    error: headerError
  } = useGetDeviceProfileHeader(id);
  const {
    actions
  } = useDeviceDetailFormStore();
  const saveMutation = useSaveDeviceStatus(id, {
    onError: (apiError: ApiError) => actions.onApiError(apiError)
  });
  const readOnly = headerData?.status === 'DECOMMISSIONED';
  const dataSections = data?.sections;
  const {
    currentStatus,
    schedules
  } = useMemo(() => splitStatusSections(dataSections ?? []), [dataSections]);
  if (isPending || isHeaderPending) {
    return <ViewportLoader />;
  }
  const blockingError = error ?? headerError ?? null;
  if (blockingError && (!data || !headerData)) {
    return <SectionDetailFetchError error={blockingError} />;
  }
  if (!data || !headerData) {
    return null;
  }
  if (schedules.length > 0) {
    return <StatusTimeline currentStatusSections={currentStatus} scheduleSections={schedules} isPending={false} readOnly={readOnly} saveMutateAsync={saveMutation.mutateAsync} />;
  }
  return <div className="px-12 py-6 max-md:p-6">
      <DeviceDetailSectionForm data={data} isPending={isPending} error={error} readOnly={readOnly} showNavigation={false} saveMutateAsync={saveMutation.mutateAsync} />
    </div>;
}